// Iter 58/59/60 — Object Drill-Down Chat: wording for the per-message object_context the backend
// stores on assistant turns (object note, compare note + ambiguity chips).
import { compareDrawerItem, getCompareDrawerLink, impactDrawerItem, labelCompareDrawerItem } from "@/lib/compareDrawerLinks";

function sideName(s) {
  if (!s || typeof s !== "object") return null;
  if (s.qualified) return String(s.qualified);
  if (!s.name) return null;
  return s.library ? `${s.library}/${s.name}` : String(s.name);
}

/** "LEFT vs RIGHT" for an Iter 60 compare context, or null. */
export function compareLabel(oc) {
  const cmp = oc && typeof oc === "object" && oc.compare && typeof oc.compare === "object" ? oc.compare : null;
  const left = cmp ? sideName(cmp.left) : null;
  const right = cmp ? sideName(cmp.right) : null;
  return left && right ? `${left} vs ${right}` : null;
}

/** Returns { used, compare, ambiguous } strings (or null) for an assistant message's object_context. */
export function objectContextNote(oc) {
  if (!oc || typeof oc !== "object") return null;
  const objects = Array.isArray(oc.objects) ? oc.objects.filter(Boolean) : [];
  const ambiguous = Array.isArray(oc.ambiguous) ? oc.ambiguous.filter(Boolean) : [];
  const compare = compareLabel(oc);
  if (!objects.length && !ambiguous.length && !compare) return null;
  return {
    used: !compare && objects.length ? `Object-focused context used: ${objects.join(", ")}` : null,
    compare: compare ? `Object comparison context used: ${compare}` : null,
    ambiguous: ambiguous.length
      ? `Ambiguous object name${ambiguous.length === 1 ? "" : "s"}: ${ambiguous.join(", ")} — specify library or type.`
      : null,
    truncated: Boolean(oc.truncated),
  };
}

// Iter 61 — compact comparison summary (counts only; never parses the answer text).
const REF_SINGULAR = {
  callers: "caller",
  "programs using file": "program using file",
  "programs referencing field": "program referencing field",
  "referencing programs": "referencing program",
};

function count(n) {
  return n === null || n === undefined || Number.isNaN(Number(n)) ? null : Number(n);
}

function part(n, singular, plural) {
  const v = count(n);
  return v && v > 0 ? `${v} ${v === 1 ? singular : plural}` : null;
}

function cell(n) {
  const v = count(n);
  return v === null ? "n/a" : String(v);
}

function listOrNone(parts) {
  return parts.filter(Boolean).join(", ") || "none";
}

/** Structured panel content for a compare turn: { label, compact: [{key,label,value}], rows: [{key,label,cells:[{k,v,items?}]}] } or null.
 *  opts.workspaceId is the session's workspace — used for drawer links when the message predates compare_context.workspace_id. */
export function compareSummary(oc, opts = {}) {
  const cc = oc && typeof oc === "object" && oc.compare_context && typeof oc.compare_context === "object" ? oc.compare_context : null;
  const s = cc && cc.used && cc.summary && typeof cc.summary === "object" ? cc.summary : null;
  if (!s) return null;
  const rules = s.rules || {};
  const deps = s.dependencies || {};
  const fields = s.fields || {};
  const screens = s.screens || {};
  const ev = s.evidence || {};
  const w = s.warnings || {};
  const d = s.dispositions || {};
  const refLabel = deps.referencing_label || "callers";
  const filesLabel = deps.files_label || "files used";
  const filesSingular = filesLabel === "files containing field" ? "file containing field" : "file";
  const filesPlural = filesLabel === "files containing field" ? "files containing field" : "files";
  const side = (key) => listOrNone([
    part(rules[key], "rule", "rules"),
    part(fields[key], "field", "fields"),
    part(deps[`${key}_callers`], REF_SINGULAR[refLabel] || refLabel, refLabel),
    part(deps[`${key}_called`], "called program", "called programs"),
    part(deps[`${key}_files`], filesSingular, filesPlural),
    key === "shared" ? part(screens.shared_formats, "screen format", "screen formats") : null,
  ]);
  const stateOf = (state, stale) => (state ? `${state}${stale ? ", stale" : ""}` : "none");
  const compact = [
    { key: "shared", label: "Shared", value: side("shared") },
    { key: "left-only", label: "Left only", value: side("left_only") },
    { key: "right-only", label: "Right only", value: side("right_only") },
    {
      key: "warnings", label: "Warnings",
      value: listOrNone([
        part(w.stale_dispositions, "stale disposition", "stale dispositions"),
        part(w.ambiguous_mappings, "ambiguous mapping", "ambiguous mappings"),
        part(w.unresolved_screens, "unresolved screen step", "unresolved screen steps"),
      ]),
    },
    {
      key: "evidence", label: "Evidence",
      value: listOrNone([part(ev.batch_count, "batch", "batches"), part(ev.unknown_provenance_count, "row without provenance", "rows without provenance")]),
    },
  ];
  const three = (obj, prefix = "") => [
    { k: "Shared", v: cell(obj[`${prefix ? `shared_${prefix}` : "shared"}`]) },
    { k: "Left only", v: cell(obj[`${prefix ? `left_only_${prefix}` : "left_only"}`]) },
    { k: "Right only", v: cell(obj[`${prefix ? `right_only_${prefix}` : "right_only"}`]) },
  ];
  // Iter 62 — names behind the counts (expanded panel only); null list = section n/a for that side
  const det = cc.details && typeof cc.details === "object" ? cc.details : null;
  const dRules = (det && det.rules) || {};
  const dDeps = (det && det.dependencies) || {};
  const dFields = (det && det.fields) || {};
  const dScreens = (det && det.screens) || {};
  const dEv = (det && det.evidence) || {};
  const dWarn = (det && det.warnings) || {};
  // Iter 65 — every name becomes a structured item with an optional workspace-bound evidence link
  const workspaceId = (cc.workspace_id && String(cc.workspace_id)) || (opts.workspaceId ? String(opts.workspaceId) : null);
  const CELL_KEY = { Shared: "shared", "Left only": "left_only", "Right only": "right_only", Ambiguous: "ambiguous", Left: "left", Right: "right",
    "Shared formats": "shared", Batches: "batches", Total: "total" };
  const sideFor = (cellKey) => (cellKey === "left_only" || cellKey === "left" ? cc.left : cellKey === "right_only" || cellKey === "right" ? cc.right : null);
  const itemsFor = (row, cellKey, names) => names.map((n, i) => {
    const sideObj = sideFor(cellKey);
    const it = compareDrawerItem(n, {
      row, cell: cellKey, sideKind: sideObj ? sideObj.kind : null, sideName: sideObj ? sideObj.name : null,
      batchId: row === "evidence" && Array.isArray(dEv.batch_ids) ? dEv.batch_ids[i] || null : null,
    });
    const link = getCompareDrawerLink(it, workspaceId);
    return { label: labelCompareDrawerItem(it) || n, kind: it ? it.kind : "text", to: link ? link.to : null, destination: link ? link.destination : null };
  });
  const withNames = (cells, lists, layout = "inline", row = null) =>
    cells.map((c, i) => {
      const l = lists[i];
      if (!(l && typeof l === "object" && Array.isArray(l.names))) return c;
      const names = l.names.map(String);
      return { ...c, names, more: count(l.more) || 0, layout, items: row ? itemsFor(row, CELL_KEY[c.k] || "", names) : undefined };
    });
  const dispositionCell = (k, sideObj, state, stale) => {
    const base = { k, v: stateOf(state, stale) };
    const link = state && sideObj ? getCompareDrawerLink({ kind: "disposition", name: sideObj.name, state, objectKind: sideObj.kind }, workspaceId) : null;
    return link ? { ...base, link } : base;
  };
  const rows = [
    { key: "rules", label: "Rules", cells: withNames(three(rules), [dRules.shared_names, dRules.left_only_names, dRules.right_only_names], "inline", "rules") },
    {
      key: "dispositions", label: "Dispositions",
      cells: [dispositionCell("Left", cc.left, d.left_state, d.left_stale), dispositionCell("Right", cc.right, d.right_state, d.right_stale), { k: "Stale", v: cell(d.stale_count) }],
    },
    {
      key: "callers", label: refLabel.charAt(0).toUpperCase() + refLabel.slice(1),
      cells: withNames(three(deps, "callers"), [dDeps.shared_callers, dDeps.left_only_callers, dDeps.right_only_callers], "inline", "callers"),
    },
    { key: "called", label: "Called programs", cells: withNames(three(deps, "called"), [dDeps.shared_called, dDeps.left_only_called, dDeps.right_only_called], "inline", "called") },
    {
      key: "files", label: filesLabel.charAt(0).toUpperCase() + filesLabel.slice(1),
      cells: withNames(three(deps, "files"), [dDeps.shared_files, dDeps.left_only_files, dDeps.right_only_files], "inline", "files"),
    },
    {
      key: "fields", label: "Fields",
      cells: withNames([...three(fields), { k: "Ambiguous", v: cell(fields.ambiguous) }],
        [dFields.shared_names, dFields.left_only_names, dFields.right_only_names, dFields.ambiguous_names], "inline", "fields"),
    },
    {
      key: "screens", label: "Screen usage",
      cells: withNames(
        [{ k: "Left", v: cell(screens.left_count) }, { k: "Right", v: cell(screens.right_count) }, { k: "Shared formats", v: cell(screens.shared_formats) }],
        [dScreens.left_formats, dScreens.right_formats, dScreens.shared_formats], "inline", "screens",
      ),
    },
    {
      key: "evidence", label: "Evidence",
      cells: withNames([{ k: "Batches", v: cell(ev.batch_count) }, { k: "Without provenance", v: cell(ev.unknown_provenance_count) }], [dEv.batch_labels], "list", "evidence"),
    },
    {
      key: "warnings", label: "Warnings",
      cells: withNames([{ k: "Total", v: cell(w.total) }, { k: "Stale", v: cell(w.stale_dispositions) }, { k: "Ambiguous mappings", v: cell(w.ambiguous_mappings) },
        { k: "Unresolved screens", v: cell(w.unresolved_screens) }], [dWarn.items], "list", "warnings"),
    },
  ];
  return { label: compareLabel({ compare: cc }) || compareLabel(oc), compact, rows, hasNames: Boolean(det), workspaceId, impact: impactOverlap(cc, workspaceId, compact) };
}

/** Iter 66 — { used, header, totals, larger, rows, warnings, unavailable } from compare_context.graph_delta, or null when absent. */
export function impactOverlap(cc, workspaceId, compact) {
  const gd = cc && cc.graph_delta && typeof cc.graph_delta === "object" ? cc.graph_delta : null;
  if (!gd) return null;
  const depth = count(gd.depth) ?? 2;
  const warnings = Array.isArray(gd.warnings) ? gd.warnings.map(String) : [];
  const header = `Impact overlap (impact graph, depth ${depth}, bounded)`;
  if (!gd.used || !gd.shared || !gd.left_only || !gd.right_only) {
    return { used: false, header, depth, rows: [], warnings, unavailable: warnings.length ? warnings.join(" ") : "Impact graph delta not recorded for this comparison." };
  }
  const named = (k, block) => {
    if (!block || !Array.isArray(block.names)) return { k, v: "n/a" };
    const names = block.names.map(String);
    const kinds = Array.isArray(block.kinds) ? block.kinds : [];
    const items = names.map((n, i) => {
      const it = impactDrawerItem(n, kinds[i]);
      const link = getCompareDrawerLink(it, workspaceId);
      return { label: (it && it.label) || n, kind: it ? it.kind : "text", to: link ? link.to : null, destination: link ? link.destination : null };
    });
    return { k, v: cell(block.total ?? names.length), names, items, more: count(block.more) || 0, layout: "inline" };
  };
  const group = (key, label, g) => ({
    key, label,
    cells: [named("Nodes", g.nodes), named("Direct callers", g.callers), named("Directly referenced", g.referenced), named("Fields", g.fields)],
  });
  const cats = (g) => Object.entries(g.edge_categories || {}).map(([k, v]) => `${k} ${v}`).join(", ") || "none";
  const left = gd.left || {};
  const right = gd.right || {};
  const ln = count(left.node_count) ?? 0;
  const rn = count(right.node_count) ?? 0;
  const larger = gd.larger_side === "equal" ? `equal (${ln} nodes each)` : gd.larger_side === "left" ? `Left (${ln} vs ${rn} nodes)` : gd.larger_side === "right" ? `Right (${rn} vs ${ln} nodes)` : null;
  const totals = `Left graph ${ln} nodes, ${count(left.edge_count) ?? 0} edges · Right graph ${rn} nodes, ${count(right.edge_count) ?? 0} edges`;
  if (Array.isArray(compact)) {
    compact.push({ key: "impact", label: "Impact", value: `${gd.shared.node_count} shared, ${gd.left_only.node_count} left only, ${gd.right_only.node_count} right only (depth ${depth})` });
  }
  return {
    used: true, header, depth, totals, larger, warnings, unavailable: null,
    rows: [
      group("impact-shared", "Shared blast radius", gd.shared),
      group("impact-left", "Left only", gd.left_only),
      group("impact-right", "Right only", gd.right_only),
      { key: "impact-edges", label: "Edge types", cells: [{ k: "Shared", v: cats(gd.shared) }, { k: "Left only", v: cats(gd.left_only) }, { k: "Right only", v: cats(gd.right_only) }] },
    ],
  };
}

/** "A, B, C, +2 more" for a named cell; "none" when the list is truly empty. */
export function namesText(c) {
  if (!c || !Array.isArray(c.names)) return null;
  if (!c.names.length) return c.more > 0 ? `+${c.more} more` : "none";
  return c.names.join(", ") + (c.more > 0 ? `, +${c.more} more` : "");
}

const TYPE_WORD = {
  "physical-file": "physical file",
  "logical-file": "logical file",
  "display-file": "display file",
  program: "program",
  field: "field",
  file: "file",
  object: "object",
};

function qualified(c) {
  return c.library ? `${c.library}/${c.name}` : c.name;
}

/** Fallback when the backend did not ship a ready-made clarified question. */
export function clarifiedQuestion(c) {
  if (c.question) return c.question;
  const word = TYPE_WORD[c.kind] || c.kind || "object";
  return c.library ? `Tell me about ${qualified(c)}.` : `Tell me about ${word} ${c.name}.`;
}

/** Chips for an ambiguous object_context: { chips: [{ key, text, aria, title, question }], more } or null. */
export function ambiguityChips(oc) {
  if (!oc || typeof oc !== "object") return null;
  const cands = Array.isArray(oc.ambiguity_candidates) ? oc.ambiguity_candidates.filter((c) => c && c.name) : [];
  if (!cands.length) return null;
  const chips = cands.map((c, i) => {
    const name = qualified(c);
    const type = c.object_type || TYPE_WORD[c.kind] || c.kind || "object";
    const label = c.label ? String(c.label) : "";
    return {
      key: `${c.hint || ""}|${name}|${c.kind || ""}|${i}`,
      text: `${name} · ${type}`,
      title: label || `${name} (${type})`,
      aria: `Ask about ${name}, ${TYPE_WORD[c.kind] || type}${label ? `, ${label}` : ""}`,
      question: clarifiedQuestion(c),
    };
  });
  const more = Number(oc.ambiguity_more || 0);
  return { chips, more, moreText: more > 0 ? `+${more} more; qualify by library or type.` : null };
}
